// Shared vocabulary for the dashboard's analytics period. The backend is the
// authority on what a period *means* (see buildPeriod in
// dashboard.controller.js); this file only builds the request and describes a
// selection locally so the UI can label itself before the response lands.

export const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const pad = (n) => String(n).padStart(2, '0');

export const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const isYmd = (v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);

export const daysInMonth = (y, m) => new Date(y, m, 0).getDate();

export const addDays = (ymd, n) => {
  const [y, m, d] = ymd.split('-').map(Number);
  return toYmd(new Date(y, m - 1, d + n));
};

export const monthRange = (y, m) => ({
  from: `${y}-${pad(m)}-01`,
  to: `${y}-${pad(m)}-${pad(daysInMonth(y, m))}`,
});

// A period is held as {from, to} everywhere — a month is simply the range that
// spans it. One shape means one code path for the URL, the request and the
// label.
export const monthOf = (ymd) => {
  const [y, m] = ymd.split('-').map(Number);
  return { y, m };
};

export const isWholeMonth = ({ from, to }) => {
  if (!isYmd(from) || !isYmd(to)) return false;
  const a = monthOf(from);
  const b = monthOf(to);
  const [, , d1] = from.split('-').map(Number);
  const [, , d2] = to.split('-').map(Number);
  return a.y === b.y && a.m === b.m && d1 === 1 && d2 === daysInMonth(a.y, a.m);
};

const dayLabel = (ymd) => {
  const [y, m, d] = ymd.split('-').map(Number);
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d)}/${p(m)}/${y}`;
};

// Mirrors the backend's labelling so the control and the payload never
// disagree about what the selected period is called.
export const describe = (period) => {
  if (!period?.from || !period?.to) return 'This month';
  if (isWholeMonth(period)) {
    const { y, m } = monthOf(period.from);
    return `${MONTHS[m - 1]} ${y}`;
  }
  if (period.from === period.to) return dayLabel(period.from);
  return `${dayLabel(period.from)} – ${dayLabel(period.to)}`;
};

// The quick picks. `range` is evaluated against a supplied "today" so the list
// stays pure and testable.
export const PRESETS = [
  {
    key: 'this-month',
    label: 'This month',
    range: (t) => monthRange(t.getFullYear(), t.getMonth() + 1),
  },
  {
    key: 'last-month',
    label: 'Last month',
    range: (t) => {
      const y = t.getMonth() === 0 ? t.getFullYear() - 1 : t.getFullYear();
      const m = t.getMonth() === 0 ? 12 : t.getMonth();
      return monthRange(y, m);
    },
  },
  {
    key: 'last-30',
    label: 'Last 30 days',
    range: (t) => ({ from: addDays(toYmd(t), -29), to: toYmd(t) }),
  },
  {
    key: 'last-90',
    label: 'Last 90 days',
    range: (t) => ({ from: addDays(toYmd(t), -89), to: toYmd(t) }),
  },
  {
    key: 'this-year',
    label: 'This year',
    range: (t) => ({ from: `${t.getFullYear()}-01-01`, to: `${t.getFullYear()}-12-31` }),
  },
  {
    key: 'last-year',
    label: 'Last year',
    range: (t) => ({ from: `${t.getFullYear() - 1}-01-01`, to: `${t.getFullYear() - 1}-12-31` }),
  },
];

export const defaultPeriod = (today = new Date()) =>
  monthRange(today.getFullYear(), today.getMonth() + 1);

export const isDefaultPeriod = (period, today = new Date()) => {
  const d = defaultPeriod(today);
  return period?.from === d.from && period?.to === d.to;
};

export const matchingPresetKey = (period, today = new Date()) =>
  PRESETS.find((p) => {
    const r = p.range(today);
    return r.from === period?.from && r.to === period?.to;
  })?.key || null;

// Step a period one whole unit backwards or forwards: a month for a month, its
// own length for a range. Keeps the arrows meaningful for both modes.
export const shift = (period, direction) => {
  if (isWholeMonth(period)) {
    const { y, m } = monthOf(period.from);
    const raw = m + direction;
    const ny = raw < 1 ? y - 1 : raw > 12 ? y + 1 : y;
    const nm = raw < 1 ? 12 : raw > 12 ? 1 : raw;
    return monthRange(ny, nm);
  }
  const [y1, m1, d1] = period.from.split('-').map(Number);
  const [y2, m2, d2] = period.to.split('-').map(Number);
  const span =
    Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86400000) + 1;
  return {
    from: addDays(period.from, span * direction),
    to: addDays(period.to, span * direction),
  };
};
